import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Download, ChevronLeft, ChevronRight,
  Calendar, Search, RefreshCw, AlertCircle, CheckCircle2, Clock, PieChart, Edit2, Send, X, Plus, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_META = {
  healthy: { color: '#10B981', label: 'Healthy', bg: 'bg-[#10B981]/10' },
  low_balance: { color: '#F59E0B', label: 'Low Balance', bg: 'bg-[#F59E0B]/10' },
  overspent: { color: '#E63946', label: 'Overspent', bg: 'bg-[#E63946]/10' },
  no_budget: { color: '#6B7280', label: 'No Budget', bg: 'bg-[#6B7280]/10' },
};

function fmtMyr(v) {
  return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ymToLabel(ym) {
  const [y, m] = ym.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function StatCard({ icon: Icon, label, value, sub, color = '#10B981', testId }) {
  return (
    <div data-testid={testId} className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">
        <Icon size={12} style={{ color }} /> {label}
      </div>
      <p className="text-2xl font-bold text-white tabular-nums tracking-tight">
        <span className="text-xs text-[#9CA3AF] mr-1">RM</span>{fmtMyr(value)}
      </p>
      {sub && <div className="text-[10px] text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );
}

function MonthPicker({ month, setMonth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);
  const [y, m] = month.split('-').map(Number);
  const [year, setYear] = useState(y);
  useEffect(() => { setYear(y); }, [y, month]);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="month-picker-btn"
        className="flex items-center gap-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[#10B981]/40 rounded-lg px-3.5 py-2 text-sm text-white transition">
        <Calendar size={13} className="text-[#10B981]" />
        <span className="font-medium">{ymToLabel(month)}</span>
        <ChevronRight size={12} className={`text-[#6B7280] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl shadow-2xl p-3 w-64" data-testid="month-picker-popover">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setYear(year - 1)} className="p-1.5 hover:bg-[hsl(var(--surface-elevated))] rounded-md text-[#9CA3AF]" data-testid="year-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="text-sm font-semibold text-white tabular-nums">{year}</span>
            <button onClick={() => setYear(year + 1)} className="p-1.5 hover:bg-[hsl(var(--surface-elevated))] rounded-md text-[#9CA3AF]" data-testid="year-next">
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {monthNames.map((name, i) => {
              const target = `${year}-${String(i + 1).padStart(2, '0')}`;
              const isActive = target === month;
              const isCurrent = `${y}-${String(m).padStart(2, '0')}` === target;
              return (
                <button
                  key={i}
                  data-testid={`month-picker-${target}`}
                  onClick={() => { setMonth(target); setOpen(false); }}
                  className={`px-2.5 py-2 rounded-md text-xs font-medium transition ${
                    isActive
                      ? 'bg-gradient-to-br from-[#10B981] to-[#3B82F6] text-white shadow-[0_2px_8px_rgba(16,185,129,0.4)]'
                      : isCurrent
                      ? 'bg-[hsl(var(--surface-elevated))] text-white ring-1 ring-[#10B981]/40'
                      : 'text-[#9CA3AF] hover:bg-[hsl(var(--surface-elevated))] hover:text-white'
                  }`}>
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AllocationEditModal({ open, onClose, channel, month, currentAllocation, onSaved }) {
  const [amount, setAmount] = useState(0);
  useEffect(() => { if (open) setAmount(currentAllocation || 0); }, [open, currentAllocation]);
  const save = async () => {
    try {
      await axios.put(`${API}/finance/channel-budgets/${channel.channel_id}/${month}/allocation`,
        { monthly_allocation: Number(amount) }, h());
      toast.success(`${channel.channel_name} budget set: RM ${fmtMyr(amount)}`);
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="allocation-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wallet size={16} className="text-[#10B981]" />
            Set Budget — {channel?.channel_name}
          </DialogTitle>
          <p className="text-[11px] text-[#6B7280] mt-1">{ymToLabel(month)} · monthly allocation cap</p>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Budget Allocation (RM)</label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="allocation-amount-input" autoFocus
              className="bg-transparent border-[hsl(var(--border))] text-white text-lg tabular-nums" />
            <p className="text-[10px] text-[#6B7280] mt-1.5">Ini target / cap budget bulanan untuk channel ini. Disbursement actual datang dari Fund Request bila approved.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="allocation-save-btn"
            className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white hover:opacity-90">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualSpendModal({ open, onClose, channel, month, onSaved }) {
  const [form, setForm] = useState({ date: '', category: '', amount: '', description: '' });
  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm({ date: today, category: 'Influencer', amount: '', description: '' });
  }, [open]);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount mesti > 0'); return; }
    try {
      await axios.post(`${API}/finance/channel-budgets/${channel.channel_id}/${month}/spend`, {
        date: form.date, category: form.category || 'Manual',
        amount: Number(form.amount), description: form.description,
      }, h());
      toast.success('Manual spend added');
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="manual-spend-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <TrendingDown size={16} className="text-[#E63946]" />
            Add Manual Spend · {channel?.channel_name}
          </DialogTitle>
          <p className="text-[10px] text-[#6B7280] mt-1">Untuk spend luar yang TAK ada dalam Daily Data marketing (cth: influencer fees, agency, ads bukan FB)</p>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="spend-date" className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Amount (RM) *</label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="spend-amount" placeholder="1500.00" autoFocus
                className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="spend-category"
              className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2">
              {['Influencer', 'Agency Fee', 'Tools / Subscription', 'Content Creation', 'Other Ads', 'Misc'].map(c =>
                <option key={c} value={c}>{c}</option>
              )}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. KOL XYZ campaign" data-testid="spend-desc"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="spend-save"
            className="bg-gradient-to-r from-[#E63946] to-[#F59E0B] text-white hover:opacity-90">Add Spend</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailModal({ open, onClose, channel, month, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spendModal, setSpendModal] = useState(false);

  const load = async () => {
    if (!channel || !open) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/channel-budgets/${channel.channel_id}/${month}`, h());
      setData(r.data);
    } catch { toast.error('Failed to load detail'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [open, channel, month]);

  const removeSpend = async (id) => {
    if (!window.confirm('Padam manual spend entry ni?')) return;
    try {
      await axios.delete(`${API}/finance/channel-budgets/${channel.channel_id}/${month}/spend/${id}`, h());
      toast.success('Removed');
      load();
    } catch { toast.error('Failed'); }
  };

  if (!channel) return null;
  const avatarUrl = channel.brand_logo_url;
  const initials = (channel.channel_name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="channel-budget-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2.5">
            <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-[hsl(var(--border))] bg-gradient-to-br from-[#10B981]/20 to-[#3B82F6]/10">
              {avatarUrl ? (
                <img src={avatarUrl} alt={channel.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br from-[#10B981] to-[#3B82F6]">
                  {initials}
                </div>
              )}
            </div>
            <div>
              <div>{channel.channel_name}</div>
              <p className="text-[11px] text-[#6B7280] mt-0.5 font-normal">{ymToLabel(month)}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-12 text-center text-[#6B7280] text-sm">Loading...</div>}
        {!loading && data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <StatCard icon={Wallet} label="Allocation" value={data.allocation} sub="Budget cap" color="#8B5CF6" testId="detail-allocation" />
              <StatCard icon={TrendingUp} label="Disbursed" value={data.disbursed} sub="from Fund Request" color="#10B981" testId="detail-disbursed" />
              <StatCard icon={TrendingDown} label="Auto Spend" value={data.auto_spend} sub="from Daily Data" color="#3B82F6" testId="detail-auto-spend" />
              <StatCard icon={Edit2} label="Manual Spend" value={data.manual_spend} sub="External entries" color="#F59E0B" testId="detail-manual-spend" />
              <StatCard icon={PieChart} label="Balance" value={data.balance} color={data.balance >= 0 ? '#10B981' : '#E63946'} testId="detail-balance" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { onClose(); navigate('/finance/fund-request'); }} size="sm"
                data-testid="detail-goto-fund-request-btn"
                className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white hover:opacity-90">
                <Send size={12} /> Request Fund untuk {channel.channel_name}
              </Button>
              <Button onClick={() => setSpendModal(true)} size="sm" variant="outline"
                data-testid="detail-add-spend-btn"
                className="text-[#F59E0B] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10">
                <Plus size={12} /> Add Manual Spend
              </Button>
            </div>

            <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[hsl(var(--border))] text-xs font-medium text-white flex items-center gap-2">
                <Clock size={12} /> Transaction Timeline · {(data.timeline || []).length} entries
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--surface))] sticky top-0 z-10">
                    <tr className="text-left text-[#6B7280] uppercase tracking-wider text-[10px]">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-2 py-2">Type</th>
                      <th className="px-2 py-2">Category</th>
                      <th className="px-2 py-2">Description</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                      <th className="px-2 py-2 text-right">Balance</th>
                      <th className="px-2 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.timeline || []).length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-[#6B7280]">
                        No transactions yet. Request fund via Fund Request, dan spend dari Daily Data marketing tab akan auto-reflect.
                      </td></tr>
                    )}
                    {(data.timeline || []).map((t, i) => {
                      const isDisburse = t.type === 'disbursement';
                      const isManual = t.source === 'manual';
                      return (
                        <tr key={t.id || i} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]/50 transition">
                          <td className="px-4 py-2 text-[#9CA3AF] tabular-nums">{t.date || '—'}</td>
                          <td className="px-2 py-2">
                            <Badge className={`text-[9px] border-0 ${isDisburse ? 'bg-[#10B981]/15 text-[#10B981]' : isManual ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#E63946]/15 text-[#E63946]'}`}>
                              {isDisburse ? 'fund in' : isManual ? 'manual' : 'spend'}
                            </Badge>
                          </td>
                          <td className="px-2 py-2 text-white">{t.category}</td>
                          <td className="px-2 py-2 text-[#9CA3AF] max-w-[260px] truncate">{t.description || '—'}</td>
                          <td className={`px-2 py-2 text-right font-bold tabular-nums ${isDisburse ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                            {isDisburse ? '+' : '−'}RM {fmtMyr(t.amount)}
                          </td>
                          <td className="px-2 py-2 text-right text-white tabular-nums">RM {fmtMyr(t.balance_after)}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-between gap-1.5">
                              <span className="text-[9px] text-[#6B7280] italic">
                                {t.source === 'fund_request' ? 'Fund Req' : t.source === 'manual' ? 'Manual' : 'Daily Data'}
                              </span>
                              {isManual && (
                                <button onClick={() => removeSpend(t.id)}
                                  className="p-1 hover:bg-[#E63946]/15 rounded text-[#E63946]"
                                  data-testid={`del-spend-${t.id}`}>
                                  <Trash2 size={10} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <ManualSpendModal open={spendModal} channel={channel} month={month}
          onClose={() => setSpendModal(false)} onSaved={load} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ChannelBudgetPage() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailChannel, setDetailChannel] = useState(null);
  const [allocChannel, setAllocChannel] = useState(null);
  const navigate = (path) => { window.location.href = path; };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/channel-budgets?month=${month}`, h());
      setData(r.data);
    } catch (e) { toast.error('Failed to load channel budgets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]);

  const filteredChannels = useMemo(() => {
    if (!data?.channels) return [];
    let list = data.channels;
    if (statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => (c.channel_name + ' ' + (c.owner_name || '')).toLowerCase().includes(q));
    }
    return list;
  }, [data, search, statusFilter]);

  const exportXlsx = async () => {
    try {
      const r = await axios.get(`${API}/finance/channel-budgets/export?month=${month}`, { ...h(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `channel_budget_${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <ResponsiveLayout>
      <div className="space-y-5 p-4 md:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#3B82F6] flex items-center justify-center shadow-[0_4px_20px_rgba(16,185,129,0.3)]">
                <PieChart size={16} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl text-white font-semibold tracking-tight">Channel Budget</h1>
                <p className="text-[11px] text-[#6B7280] mt-0.5">Underads fund — disbursement dari Fund Request · spend auto dari Daily Data marketing</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MonthPicker month={month} setMonth={setMonth} />
            <Button onClick={load} size="sm" variant="outline" data-testid="refresh-btn">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </Button>
            <Button onClick={exportXlsx} size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40" data-testid="export-btn">
              <Download size={12} /> Export
            </Button>
          </div>
        </div>

        {data?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Wallet} label="Total Allocation" value={data.summary.total_allocation} sub={`${data.summary.channels_count} channels`} color="#8B5CF6" testId="summary-allocation" />
            <StatCard icon={TrendingUp} label="Total Disbursed" value={data.summary.total_disbursed} sub="from approved fund requests" color="#10B981" testId="summary-disbursed" />
            <StatCard icon={TrendingDown} label="Total Spend" value={data.summary.total_spend}
              sub={`auto RM${fmtMyr(data.summary.auto_spend || 0)} · manual RM${fmtMyr(data.summary.manual_spend || 0)}`}
              color="#3B82F6" testId="summary-spend" />
            <StatCard icon={PieChart} label="Balance" value={data.summary.balance} color={data.summary.balance >= 0 ? '#10B981' : '#E63946'} testId="summary-balance" />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search channel..."
              data-testid="search-input"
              className="bg-transparent border-[hsl(var(--border))] text-white text-xs h-8 pl-7" />
          </div>
          <div className="flex items-center gap-1 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-0.5">
            {['all', 'healthy', 'low_balance', 'overspent', 'no_budget'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`status-filter-${s}`}
                className={`px-2.5 py-1 rounded text-[11px] font-medium transition ${
                  statusFilter === s ? 'bg-[hsl(var(--surface-elevated))] text-white' : 'text-[#9CA3AF] hover:text-white'
                }`}>
                {s === 'all' ? 'All' : STATUS_META[s]?.label || s}
              </button>
            ))}
          </div>
          {(data?.summary?.low_balance_count + data?.summary?.overspent_count) > 0 && (
            <div className="ml-auto flex items-center gap-1.5 text-[11px] text-[#F59E0B]">
              <AlertCircle size={12} />
              <span>{data?.summary?.low_balance_count} low · {data?.summary?.overspent_count} overspent</span>
            </div>
          )}
        </div>

        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--surface-elevated))]">
                <tr className="text-left text-[#6B7280] uppercase tracking-wider text-[10px]">
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-2 py-3 text-right">Budget (RM)</th>
                  <th className="px-2 py-3 text-right">Disbursed</th>
                  <th className="px-2 py-3 text-right">Total Spend</th>
                  <th className="px-2 py-3 text-right">Balance</th>
                  <th className="px-2 py-3">% Used</th>
                  <th className="px-2 py-3">Status</th>
                  <th className="px-2 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="text-center py-12 text-[#6B7280]">Loading...</td></tr>}
                {!loading && filteredChannels.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-[#6B7280]">No channels match the filter</td></tr>
                )}
                {!loading && filteredChannels.map(c => {
                  const meta = STATUS_META[c.status] || STATUS_META.healthy;
                  const usagePctClamped = Math.min(100, c.usage_pct || 0);
                  const avatarUrl = c.brand_logo_url;
                  const initials = (c.channel_name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                  return (
                    <tr key={c.channel_id} data-testid={`channel-row-${c.channel_id}`}
                      className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-[hsl(var(--border))] bg-gradient-to-br from-[#10B981]/20 to-[#3B82F6]/10">
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={c.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-[10px] bg-gradient-to-br from-[#10B981] to-[#3B82F6]">
                                {initials}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">{c.channel_name}</div>
                            <div className="text-[10px] text-[#6B7280] truncate">{c.owner_name || c.channel_type || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button
                          onClick={() => setAllocChannel(c)}
                          data-testid={`edit-allocation-${c.channel_id}`}
                          className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#8B5CF6]/10 transition">
                          <span className="text-[#8B5CF6] font-bold tabular-nums"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(c.allocation)}</span>
                          <Edit2 size={10} className="text-[#6B7280] group-hover:text-[#8B5CF6]" />
                        </button>
                      </td>
                      <td className="px-2 py-3 text-right text-[#10B981] tabular-nums"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(c.disbursed)}</td>
                      <td className="px-2 py-3 text-right tabular-nums">
                        <div className="text-[#3B82F6] font-bold"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(c.total_spend)}</div>
                        <div className="text-[9px] text-[#6B7280]">
                          auto RM{fmtMyr(c.auto_spend)} · manual RM{fmtMyr(c.manual_spend)}
                        </div>
                      </td>
                      <td className={`px-2 py-3 text-right font-bold tabular-nums ${c.balance >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                        {c.balance < 0 ? '−' : ''}<span className="text-[10px] mr-0.5">RM</span>{fmtMyr(Math.abs(c.balance))}
                      </td>
                      <td className="px-2 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[hsl(var(--surface-elevated))] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{
                              width: `${usagePctClamped}%`,
                              background: c.usage_pct > 100 ? '#E63946' : c.usage_pct > 80 ? '#F59E0B' : '#10B981',
                            }} />
                          </div>
                          <span className="text-[10px] text-[#9CA3AF] tabular-nums">{(c.usage_pct || 0).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <Badge className={`text-[9px] border-0 ${meta.bg}`} style={{ color: meta.color }}>{meta.label}</Badge>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <Button onClick={() => setDetailChannel(c)} size="sm" variant="outline"
                          className="text-[10px] h-6 text-[#3B82F6] border-[#3B82F6]/40"
                          data-testid={`view-detail-${c.channel_id}`}>
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filteredChannels.length > 0 && (
                <tfoot className="bg-[hsl(var(--surface-elevated))]/70 sticky bottom-0">
                  <tr className="border-t-2 border-[#10B981]/30 text-xs font-bold">
                    <td className="px-4 py-3 text-white">TOTAL ({filteredChannels.length} channels)</td>
                    <td className="px-2 py-3 text-right text-[#8B5CF6] tabular-nums"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(filteredChannels.reduce((s, c) => s + c.allocation, 0))}</td>
                    <td className="px-2 py-3 text-right text-[#10B981] tabular-nums"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(filteredChannels.reduce((s, c) => s + c.disbursed, 0))}</td>
                    <td className="px-2 py-3 text-right text-[#3B82F6] tabular-nums"><span className="text-[10px] mr-0.5">RM</span>{fmtMyr(filteredChannels.reduce((s, c) => s + c.total_spend, 0))}</td>
                    <td className="px-2 py-3 text-right tabular-nums" style={{
                      color: filteredChannels.reduce((s, c) => s + c.balance, 0) >= 0 ? '#10B981' : '#E63946'
                    }}>
                      <span className="text-[10px] mr-0.5">RM</span>{fmtMyr(filteredChannels.reduce((s, c) => s + c.balance, 0))}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <AllocationEditModal
          open={!!allocChannel}
          channel={allocChannel}
          month={month}
          currentAllocation={allocChannel?.allocation || 0}
          onClose={() => setAllocChannel(null)}
          onSaved={load}
        />
        <DetailModal
          open={!!detailChannel}
          channel={detailChannel}
          month={month}
          navigate={navigate}
          onClose={() => setDetailChannel(null)}
        />
      </div>
    </ResponsiveLayout>
  );
}
